import {
  DataNode,
  Device,
  DeviceTestEquipment,
  DeviceType,
  Examination,
  NodeDataType,
  TestData,
} from "graphql/types"
import { v4 } from "uuid"

const emptyDataNode: DataNode = {
  dataType: NodeDataType.TEXT,
  id: "",
  text: "",
  editable: false,
  deletable: false,
  image: [],
  imageCaption: [],
  imageNumber: [],
  tableBody: [],
  tableCaption: "",
  tableHeader: [],
  tableNumber: 0,
}

// Professional Header with TREO Branding
export const getTREOHeader = (): DataNode => {
  // Create a professional text-based logo header
  return {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: `\n\n# **TREO GmbH**\n\n*Labor für Umweltsimulation* | *Environmental Testing Laboratory*\n\n---\n\n`,
    editable: false,
    deletable: false,
  }
}

// Equipment Photographs Section
export const getEquipmentPhotographs = (devices?: Device[]): DataNode[] => {
  const photos: DataNode[] = []

  const introText: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: "### Equipment Photographs",
    editable: false,
    deletable: false,
  }
  photos.push(introText)

  if (devices && devices.length > 0) {
    devices.forEach((device, index) => {
      const photoPlaceholder: DataNode = {
        ...emptyDataNode,
        dataType: NodeDataType.TEXT,
        id: v4(),
        text: `**Figure ${index + 1}:** Equipment under test No. ${String(index + 1).padStart(2, "0")} - ${device.name || "Equipment"}\n\n[Equipment photo placeholder]`,
        editable: true,
        deletable: false,
      }
      photos.push(photoPlaceholder)

      const labelPhoto: DataNode = {
        ...emptyDataNode,
        dataType: NodeDataType.TEXT,
        id: v4(),
        text: `**Figure ${index + 2}:** Label of equipment under test No. ${String(index + 1).padStart(2, "0")}\n\n[Label photo placeholder]`,
        editable: true,
        deletable: false,
      }
      photos.push(labelPhoto)
    })
  }

  return photos
}

// Test Equipment Calibration Table
export const getTestEquipmentTable = (examination?: Examination): DataNode => {
  // Extract test equipment from examination data
  const testEquipment: DeviceTestEquipment[] = []

  if (examination?.deviceSetup) {
    examination.deviceSetup.forEach((setup) => {
      if (setup.device) {
        // Check if it's test equipment (TEST_EQUIPMENT type)
        if (
          (setup.device as any)?.__typename === "DeviceTestEquipment" ||
          (setup.device as any)?.type === DeviceType.TEST_EQUIPMENT
        ) {
          testEquipment.push(setup.device as DeviceTestEquipment)
        }
      }
    })
  }

  // Build table body from actual equipment or use defaults
  const tableBody =
    testEquipment.length > 0
      ? testEquipment.map((equipment) => {
          // Format calibration date properly
          const calibDueDate = equipment.calibrationDue
            ? new Date(equipment.calibrationDue).toLocaleDateString("de-DE")
            : "./."

          return [
            { data: equipment.description || equipment.name || "Test Equipment", colSpan: 1 },
            { data: equipment.model || equipment.teType || "./.", colSpan: 1 },
            { data: equipment.manufacturer || "./.", colSpan: 1 },
            { data: equipment.barcode || equipment.inventoryNo || "./.", colSpan: 1 },
            { data: calibDueDate, colSpan: 1 },
          ]
        })
      : [
          [
            { data: "Signal Generator", colSpan: 1 },
            { data: "./.", colSpan: 1 },
            { data: "./.", colSpan: 1 },
            { data: "./.", colSpan: 1 },
            { data: "./.", colSpan: 1 },
          ],
          [
            { data: "Power Meter", colSpan: 1 },
            { data: "./.", colSpan: 1 },
            { data: "./.", colSpan: 1 },
            { data: "./.", colSpan: 1 },
            { data: "./.", colSpan: 1 },
          ],
        ]

  return {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: false,
    tableCaption: "Test Equipment",
    tableHeader: [
      { data: "Description", colSpan: 1 },
      { data: "Type/Model", colSpan: 1 },
      { data: "Manufacturer", colSpan: 1 },
      { data: "Inventory No.", colSpan: 1 },
      { data: "Calibration Due", colSpan: 1 },
    ],
    tableBody,
  }
}

// Test Setup Diagrams
export const getTestSetupDiagrams = (testName: string): DataNode[] => {
  return [
    {
      ...emptyDataNode,
      dataType: NodeDataType.TEXT,
      id: v4(),
      text: `### Test Set-up for ${testName}`,
      editable: false,
      deletable: false,
    },
    {
      ...emptyDataNode,
      dataType: NodeDataType.TEXT,
      id: v4(),
      text: `**Figure:** Test set-up\n\n[Test setup diagram placeholder]\n\n_The test setup shows the configuration of equipment and connections for ${testName} testing._`,
      editable: true,
      deletable: false,
    },
  ]
}

// External Witnessing Persons Section
export const getExternalWitnessingSection = (): DataNode => {
  return {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: "### External Witnessing Persons\n\n./.  _(No external witnesses present)_",
    editable: true,
    deletable: false,
  }
}

// Test Parameters Table
export const getTestParametersTable = (examination?: Examination): DataNode => {
  return {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: false,
    tableCaption: "Test Parameters",
    tableHeader: [
      { data: "Parameter", colSpan: 1 },
      { data: "Value", colSpan: 1 },
      { data: "Comment", colSpan: 1 },
    ],
    tableBody: [
      [
        { data: "Frequency range", colSpan: 1 },
        { data: "80 MHz – 2700 MHz", colSpan: 1 },
        { data: "./.", colSpan: 1 },
      ],
      [
        { data: "Level", colSpan: 1 },
        { data: "10 V/m", colSpan: 1 },
        { data: "./.", colSpan: 1 },
      ],
      [
        { data: "Modulation", colSpan: 1 },
        { data: "80% AM, 1 kHz", colSpan: 1 },
        { data: "./.", colSpan: 1 },
      ],
      [
        { data: "Step size", colSpan: 1 },
        { data: "1%", colSpan: 1 },
        { data: "./.", colSpan: 1 },
      ],
      [
        { data: "Dwell time", colSpan: 1 },
        { data: "3 s", colSpan: 1 },
        { data: "./.", colSpan: 1 },
      ],
    ],
  }
}

// Connection Descriptions Table
export const getConnectionDescriptionsTable = (devices?: Device[]): DataNode => {
  const connections =
    devices
      ?.filter((d) => d.type === DeviceType.EQUIPMENT_UNDER_TEST)
      .map((device, idx) => [
        { data: String(idx + 1).padStart(2, "0"), colSpan: 1 },
        { data: "X1", colSpan: 1 },
        { data: "Main Power Supply", colSpan: 1 },
        { data: "./.", colSpan: 1 },
        { data: "240 V / 50 Hz", colSpan: 1 },
      ]) || []

  return {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: false,
    tableCaption: "List of connections",
    tableHeader: [
      { data: "EUT No.", colSpan: 1 },
      { data: "Port", colSpan: 1 },
      { data: "Description", colSpan: 1 },
      { data: "Port", colSpan: 1 },
      { data: "Target", colSpan: 1 },
    ],
    tableBody:
      connections.length > 0
        ? connections
        : [
            [
              { data: "01", colSpan: 1 },
              { data: "X1", colSpan: 1 },
              { data: "Main Power Supply", colSpan: 1 },
              { data: "./.", colSpan: 1 },
              { data: "240 V / 50 Hz", colSpan: 1 },
            ],
            [
              { data: "01", colSpan: 1 },
              { data: "X2", colSpan: 1 },
              { data: "Ethernet", colSpan: 1 },
              { data: "X2", colSpan: 1 },
              { data: "AE No. 01", colSpan: 1 },
            ],
            [
              { data: "01", colSpan: 1 },
              { data: "X3", colSpan: 1 },
              { data: "Case GND", colSpan: 1 },
              { data: "./.", colSpan: 1 },
              { data: "GND", colSpan: 1 },
            ],
          ],
  }
}

// Detailed Test Results Table
export const getDetailedTestResults = (testName: string, testData?: TestData[]): DataNode => {
  return {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: false,
    tableCaption: `Results for ${testName}`,
    tableHeader: [
      { data: "EUT No.", colSpan: 1 },
      { data: "Port", colSpan: 1 },
      { data: "Method", colSpan: 1 },
      { data: "Level", colSpan: 1 },
      { data: "Result", colSpan: 1 },
      { data: "Comment", colSpan: 1 },
    ],
    tableBody: [
      [
        { data: "01", colSpan: 1 },
        { data: "Casing", colSpan: 1 },
        { data: "Direct", colSpan: 1 },
        { data: "± 8 kV", colSpan: 1 },
        { data: "Criteria A", colSpan: 1 },
        { data: "./.", colSpan: 1 },
      ],
      [
        { data: "01", colSpan: 1 },
        { data: "X1", colSpan: 1 },
        { data: "Contact", colSpan: 1 },
        { data: "± 6 kV", colSpan: 1 },
        { data: "Criteria A", colSpan: 1 },
        { data: "Flickering of power LED", colSpan: 1 },
      ],
    ],
  }
}

// Measurement Uncertainty Notice
export const getMeasurementUncertaintyNotice = (): DataNode => {
  return {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: "_Expanded measurement uncertainty: XX._",
    editable: false,
    deletable: false,
  }
}

// Professional Footer
export const getProfessionalFooter = (
  documentNo: string,
  issueStatus: string,
  pageNum: number,
  totalPages: number
): DataNode => {
  return {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: `---\n\n**Document No.:** ${documentNo}\n**Issue:** ${issueStatus}\n**Page ${pageNum} of ${totalPages}**\n\n_Accredited Testing Laboratory_\n_DAkkS D-PL-xxxxx-xx-xx_`,
    editable: false,
    deletable: false,
  }
}
